import React, { useState } from "react";
import { Check, Eye, EyeOff, Lock, X } from "lucide-react";
import { changePassword } from "../api";

const initialForm = {
  contrasena_actual: "",
  nueva_contrasena: "",
  confirmar_contrasena: "",
};

const CambiarContrasena = () => {
  const [formData, setFormData] = useState(initialForm);
  const [visibleFields, setVisibleFields] = useState({});
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const password = formData.nueva_contrasena;
  const requirements = [
    { label: "Entre 8 y 72 caracteres", valid: password.length >= 8 && password.length <= 72 },
    { label: "Una letra mayúscula", valid: /[A-Z]/.test(password) },
    { label: "Una letra minúscula", valid: /[a-z]/.test(password) },
    { label: "Un número", valid: /\d/.test(password) },
    { label: "Un carácter especial: . _ o -", valid: /[._-]/.test(password) },
    { label: "Sin otros caracteres especiales", valid: /^[A-Za-z\d._-]*$/.test(password) },
  ];
  const completedRequirements = requirements.filter(({ valid }) => valid).length;
  const passwordIsValid = requirements.every(({ valid }) => valid);
  const passwordsMatch = password.length > 0 && password === formData.confirmar_contrasena;
  const formIsValid = formData.contrasena_actual && passwordIsValid && passwordsMatch;

  const handleChange = ({ target: { name, value } }) => {
    setFormData((current) => ({ ...current, [name]: value }));
    setError("");
  };

  const toggleVisibility = (field) => {
    setVisibleFields((current) => ({ ...current, [field]: !current[field] }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formIsValid) return;

    setSubmitting(true);
    setError("");
    try {
      await changePassword(formData);
      setFormData(initialForm);
      setVisibleFields({});
      setShowConfirmation(true);
    } catch (requestError) {
      setError(requestError.response?.data?.error || "No fue posible cambiar la contraseña.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderPasswordField = (name, label, autoComplete) => (
    <div className="password-field">
      <label htmlFor={name}>{label}</label>
      <div className="password-input-wrap">
        <input
          id={name}
          name={name}
          type={visibleFields[name] ? "text" : "password"}
          value={formData[name]}
          onChange={handleChange}
          autoComplete={autoComplete}
          maxLength={72}
          required
        />
        <button
          type="button"
          className="password-visibility"
          onClick={() => toggleVisibility(name)}
          aria-label={visibleFields[name] ? `Ocultar ${label.toLowerCase()}` : `Mostrar ${label.toLowerCase()}`}
          title={visibleFields[name] ? "Ocultar contraseña" : "Mostrar contraseña"}
        >
          {visibleFields[name] ? <EyeOff size={19} /> : <Eye size={19} />}
        </button>
      </div>
    </div>
  );

  return (
    <section className="password-settings" aria-labelledby="password-settings-title">
      <header className="password-settings-header">
        <Lock size={24} aria-hidden="true" />
        <div>
          <h1 id="password-settings-title">Cambiar contraseña</h1>
          <p>Actualiza la contraseña de tu cuenta.</p>
        </div>
      </header>

      <form onSubmit={handleSubmit} className="password-settings-form">
        {renderPasswordField("contrasena_actual", "Contraseña actual", "current-password")}
        {renderPasswordField("nueva_contrasena", "Nueva contraseña", "new-password")}

        <div className="password-strength" aria-live="polite">
          <div className="password-strength-heading">
            <span>Seguridad de la contraseña</span>
            <strong>{passwordIsValid ? "Cumple" : `${completedRequirements} de ${requirements.length}`}</strong>
          </div>
          <div
            className="password-strength-track"
            role="progressbar"
            aria-valuemin="0"
            aria-valuemax={requirements.length}
            aria-valuenow={completedRequirements}
          >
            <span style={{ width: `${(completedRequirements / requirements.length) * 100}%` }} />
          </div>
          <ul className="password-requirements">
            {requirements.map(({ label, valid }) => (
              <li key={label} className={valid ? "valid" : "pending"}>
                {valid ? <Check size={15} /> : <X size={15} />}
                {label}
              </li>
            ))}
          </ul>
        </div>

        {renderPasswordField("confirmar_contrasena", "Confirmar nueva contraseña", "new-password")}
        {formData.confirmar_contrasena && (
          <p className={passwordsMatch ? "password-match valid" : "password-match error"}>
            {passwordsMatch ? "Las contraseñas coinciden." : "Las contraseñas no coinciden."}
          </p>
        )}
        {error && <p className="password-request-error" role="alert">{error}</p>}

        <button type="submit" className="password-submit" disabled={!formIsValid || submitting}>
          {submitting ? "Actualizando..." : "Cambiar contraseña"}
        </button>
      </form>

      {showConfirmation && (
        <div className="modal-overlay password-confirmation-overlay" role="presentation">
          <div className="password-confirmation" role="dialog" aria-modal="true" aria-labelledby="password-confirmation-title">
            <div className="password-confirmation-icon"><Check size={28} /></div>
            <h2 id="password-confirmation-title">Contraseña actualizada</h2>
            <p>El cambio se realizó correctamente.</p>
            <button type="button" onClick={() => setShowConfirmation(false)}>Aceptar</button>
          </div>
        </div>
      )}
    </section>
  );
};

export default CambiarContrasena;